// src/pages/purchases/Purchases.js
import { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Tooltip, CircularProgress, Alert, Chip,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField, styled
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, DeleteForever as DeleteIcon
} from '@mui/icons-material';

import { ActionButton, SecondaryButton } from '../../components/common/ActionButton';
import { usePurchases } from '../../hooks/usePurchases';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import PurchaseForm from './PurchaseForm';

const PageContainer = styled(Box)(() => ({
  padding: '24px',
  backgroundColor: '#0A0A0C',
  color: 'white',
  minHeight: 'calc(100vh - 64px)'
}));

const ETIQUETA_TIPO = {
  factura: 'Factura',
  nota_credito: 'Nota de crédito',
  nota_debito: 'Nota de débito',
  importacion: 'Importación'
};

const bs = (n) => (Number(n) || 0).toLocaleString('es-VE', {
  minimumFractionDigits: 2, maximumFractionDigits: 2
});

const soloFecha = (f) => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : '');

/** Primer y último día del mes en curso, para arrancar mostrando algo útil. */
const mesActual = () => {
  const hoy = new Date();
  const anio = hoy.getFullYear();
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');
  const ultimo = new Date(anio, hoy.getMonth() + 1, 0).getDate();
  return { desde: `${anio}-${mes}-01`, hasta: `${anio}-${mes}-${ultimo}` };
};

/**
 * Compras del Libro de Compras.
 *
 * Se cargan a mano: la factura que le llega a la empresa es un papel de su
 * proveedor, y no existe todavía una integración con el SENIAT que las
 * precargue como hace ARCA en Argentina.
 */
export const Purchases = () => {
  const [periodo, setPeriodo] = useState(mesActual);
  const { purchases, loading, error, savePurchase, deletePurchase, fetchPurchases } =
    usePurchases(mesActual());

  const [abierto, setAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState(null);
  const [aBorrar, setABorrar] = useState(null);
  const [aviso, setAviso] = useState(null);

  const { canCreate, canDelete } = useRoleAccess() || {};
  const puedeCrear = !canCreate || canCreate();
  const puedeBorrar = !canDelete || canDelete();

  const cambiarPeriodo = (campo) => (e) => {
    const siguiente = { ...periodo, [campo]: e.target.value };
    setPeriodo(siguiente);
    fetchPurchases(siguiente);
  };

  const guardar = async (compra) => {
    await savePurchase(compra);
    setAviso(compra._id ? 'Compra actualizada.' : 'Compra registrada.');
  };

  const confirmarBorrado = async () => {
    try {
      await deletePurchase(aBorrar._id);
      setAviso('Compra eliminada.');
    } catch (e) {
      setAviso(e.message || 'No se pudo eliminar.');
    } finally {
      setABorrar(null);
    }
  };

  const totalDelPeriodo = purchases.reduce((s, c) => {
    const signo = c.tipoDocumento === 'nota_credito' ? -1 : 1;
    return s + (Number(c.totalConIva) || 0) * signo;
  }, 0);

  const estiloFecha = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
      '&.Mui-focused fieldset': { borderColor: '#F5A623' }
    }
  };

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Compras</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Facturas recibidas de proveedores. Alimentan el Libro de Compras.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
            value={periodo.desde} onChange={cambiarPeriodo('desde')} sx={estiloFecha} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
            value={periodo.hasta} onChange={cambiarPeriodo('hasta')} sx={estiloFecha} />
          {puedeCrear && (
            <ActionButton startIcon={<AddIcon />} onClick={() => { setEnEdicion(null); setAbierto(true); }}>
              Registrar compra
            </ActionButton>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#F5A623' }} />
        </Box>
      ) : purchases.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: '#131316', color: 'rgba(255,255,255,0.7)', backgroundImage: 'none' }}>
          <Typography variant="body1">No hay compras cargadas en este período.</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.5)' }}>
            Cargá las facturas que recibiste de tus proveedores para que aparezcan
            en el Libro de Compras y descuenten crédito fiscal.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ bgcolor: '#131316', backgroundImage: 'none', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Fecha', 'Tipo', 'Nº documento', 'Nº control', 'Proveedor', 'RIF'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#26262C', color: 'white', fontWeight: 700 }}>{h}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ bgcolor: '#26262C', color: 'white', fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#26262C', color: 'white', fontWeight: 700 }}>IVA</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#26262C', color: 'white', fontWeight: 700 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases.map((c) => {
                  const iva = (Number(c.ivaGeneral) || 0) + (Number(c.ivaReducida) || 0) + (Number(c.ivaAdicional) || 0);
                  const esCredito = c.tipoDocumento === 'nota_credito';
                  return (
                    <TableRow key={c._id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}>
                      <TableCell sx={{ color: 'white' }}>{soloFecha(c.fecha)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={ETIQUETA_TIPO[c.tipoDocumento] || c.tipoDocumento}
                          sx={{
                            bgcolor: esCredito ? 'rgba(244,67,54,0.15)' : 'rgba(255,255,255,0.08)',
                            color: esCredito ? '#ef9a9a' : 'rgba(255,255,255,0.85)'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>{c.numeroFactura}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{c.numeroControl || '—'}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{c.nombreProveedor}</TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{c.rifProveedor}</TableCell>
                      <TableCell align="right" sx={{ color: esCredito ? '#ef9a9a' : 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {esCredito ? '−' : ''}{bs(c.totalConIva)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'rgba(255,255,255,0.75)', fontVariantNumeric: 'tabular-nums' }}>
                        {bs(iva)}
                      </TableCell>
                      <TableCell align="center">
                        {puedeCrear && (
                          <Tooltip title="Editar">
                            <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}
                              onClick={() => { setEnEdicion(c); setAbierto(true); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {puedeBorrar && (
                          <Tooltip title="Eliminar">
                            <IconButton size="small" color="error" onClick={() => setABorrar(c)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {purchases.length} documento{purchases.length === 1 ? '' : 's'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
              Total del período: Bs. {bs(totalDelPeriodo)}
            </Typography>
          </Box>
        </>
      )}

      <PurchaseForm
        open={abierto}
        onClose={() => setAbierto(false)}
        purchase={enEdicion}
        onSave={guardar}
      />

      <Dialog open={!!aBorrar} onClose={() => setABorrar(null)}
        PaperProps={{ sx: { bgcolor: '#131316', color: 'white', backgroundImage: 'none' } }}>
        <DialogTitle>Eliminar compra</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.75)' }}>
            Se va a eliminar la factura {aBorrar?.numeroFactura} de {aBorrar?.nombreProveedor}.
            Deja de contar como crédito fiscal en el Libro de Compras.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <SecondaryButton onClick={() => setABorrar(null)}>Cancelar</SecondaryButton>
          <ActionButton onClick={confirmarBorrado} sx={{ bgcolor: '#d32f2f', color: 'white', '&:hover': { bgcolor: '#b71c1c' } }}>
            Eliminar
          </ActionButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!aviso} autoHideDuration={4000} onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setAviso(null)} sx={{ width: '100%' }}>{aviso}</Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Purchases;
